import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packagesAPI } from '../services/api';
import './Packages.css';

/**
 * The storefront — slice 2: the buttons work.
 *
 * A package may ask the student something after they pick it (ESSLCE asks for
 * stream; UAT asks nothing; an exit exam will ask for department). What it asks
 * is not hardcoded here — the server sends `enrollment_schema` and this file
 * renders it. Adding a program stays content plus config, which is the whole
 * contract the package system exists to keep.
 *
 * Free packages grant immediately and go to the dashboard. Paid ones come back
 * `next: 'checkout'` and land on a holding panel, because checkout is slice 3.
 * Nothing unlocks until a payment settles.
 */

type Pkg = Awaited<ReturnType<typeof packagesAPI.list>>['packages'][number];
type Field = {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string; icon?: string; hint?: string }>;
};

const priceLabel = (p: Pkg): string => {
  if (p.free) return 'Free';
  const { amount, currency } = p.pricing || {};
  if (!amount) return 'Free';
  return `${amount} ${currency === 'ETB' ? 'birr' : currency ?? ''}`.trim();
};

const ctaLabel = (p: Pkg): string => {
  if (p.cta === 'continue') return 'Continue';
  if (p.cta === 'start') return 'Start';
  // A paid package the student does not hold. The button opens the free tier,
  // not a payment screen, so it must not say "Get it — 350 birr" and then take
  // them somewhere free; the price is shown beside the card instead.
  return 'Start free';
};

/**
 * Renders one declared question. Only `single_select` exists so far; an
 * unknown type renders nothing rather than a broken control, so a program can
 * declare a field this build does not understand without breaking the picker.
 */
const SchemaField: React.FC<{
  field: Field;
  value?: string;
  onPick: (key: string, value: string) => void;
}> = ({ field, value, onPick }) => {
  if (field.type !== 'single_select' || !field.options?.length) return null;
  return (
    <fieldset className="chooser-field">
      <legend className="chooser-legend">{field.label || field.key}</legend>
      <div className="chooser-options">
        {field.options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`chooser-option${value === o.value ? ' chooser-option--on' : ''}`}
            aria-pressed={value === o.value}
            onClick={() => onPick(field.key, o.value)}
          >
            {o.icon && <span className="chooser-option__icon">{o.icon}</span>}
            <span className="chooser-option__label">{o.label}</span>
            {o.hint && <span className="chooser-option__hint">{o.hint}</span>}
          </button>
        ))}
      </div>
    </fieldset>
  );
};

const Packages: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [packages, setPackages] = useState<Pkg[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The card currently being answered, and the answers so far.
  const [choosing, setChoosing] = useState<Pkg | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const navigate = useNavigate();

  const load = useCallback(async () => {
    const data = await packagesAPI.list();
    setEnabled(data.enabled);
    setPackages(data.packages || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch {
        if (!cancelled) setError('Could not load packages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const submit = async (pkg: Pkg, picked: Record<string, string>) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await packagesAPI.select(pkg.key, picked);
      setChoosing(null);
      setAnswers({});
      if (result.next === 'start') {
        // The package says where its home is. Hardcoding /dashboard here is
        // what sent a student who just picked UAT to the ESSLCE dashboard.
        navigate(pkg.home || '/dashboard');
        return;
      }
      // Paid: the entitlement is pending and unlocks nothing yet — but the FREE
      // tier is real and immediate (the diagnostic, one lesson per section, 20
      // questions), and sending a student to a payment screen before they have
      // seen any of it is asking them to buy something they have not read a
      // word of. Land them INSIDE the package; the trial banner there carries
      // the way to pay, and /packages/:key/checkout stays a real URL for the
      // bot to deep-link to.
      navigate(pkg.home || '/dashboard');
    } catch (e: any) {
      // The server writes 400 messages for the student; show them verbatim
      // rather than replacing them with something vaguer.
      setSubmitError(
        e?.response?.data?.detail || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onCta = (pkg: Pkg) => {
    if (pkg.cta === 'continue') {
      navigate(pkg.home || '/dashboard');
      return;
    }
    const schema = (pkg.enrollment_schema || []) as Field[];
    if (schema.length === 0) {
      submit(pkg, {});
      return;
    }
    setAnswers({});
    setSubmitError(null);
    setChoosing(pkg);
  };

  const chooserSchema = ((choosing?.enrollment_schema || []) as Field[]).filter(
    (f) => f.type === 'single_select' && f.options?.length
  );
  const chooserReady = chooserSchema
    .filter((f) => f.required)
    .every((f) => Boolean(answers[f.key]));

  if (loading) {
    return (
      <div className="packages-page packages-page--centered">
        <div className="packages-spinner" aria-label="Loading" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="packages-page packages-page--centered">
        <p className="packages-muted">{error}</p>
        <button className="packages-ghost" onClick={() => navigate('/dashboard')}>
          Back to dashboard
        </button>
      </div>
    );
  }

  if (!enabled || packages.length === 0) {
    return (
      <div className="packages-page packages-page--centered">
        <h1 className="packages-title">Packages</h1>
        <p className="packages-muted">
          Nothing here yet. We&rsquo;ll let you know when there is.
        </p>
        <button className="packages-ghost" onClick={() => navigate('/dashboard')}>
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="packages-page">
      <header className="packages-header">
        <p className="packages-kicker">ጥቅሎች</p>
        <h1 className="packages-title">What are you preparing for?</h1>
        <p className="packages-sub">
          Choose one to begin.
        </p>
      </header>

      <div className="packages-grid">
        {packages.map((p) => (
          <article
            key={p.key}
            className={`package-card${p.enrolled ? ' package-card--current' : ''}`}
          >
            <div className="package-card__top">
              <h2 className="package-card__name">{p.name}</h2>
              <span
                className={`package-card__price${
                  p.free ? ' package-card__price--free' : ''
                }`}
              >
                {priceLabel(p)}
              </span>
            </div>

            {p.tagline && <p className="package-card__tagline">{p.tagline}</p>}

            {p.subjects?.length > 0 && (
              <ul className="package-card__subjects">
                {p.subjects.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            )}

            {p.enrolled && (
              <p className="package-card__badge">You&rsquo;re in this one</p>
            )}
            {!p.enrolled && p.held && !p.free && (
              <p className="package-card__badge">Yours</p>
            )}

            <button
              className="package-card__cta"
              onClick={() => onCta(p)}
              disabled={submitting}
            >
              {ctaLabel(p)}
            </button>
          </article>
        ))}
      </div>

      {choosing && (
        <div
          className="chooser-scrim"
          role="dialog"
          aria-modal="true"
          aria-label={choosing.name}
          onClick={() => !submitting && setChoosing(null)}
        >
          <div className="chooser" onClick={(e) => e.stopPropagation()}>
            <h2 className="chooser-title">{choosing.name}</h2>

            {chooserSchema.map((f) => (
              <SchemaField
                key={f.key}
                field={f}
                value={answers[f.key]}
                onPick={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))}
              />
            ))}

            {submitError && <p className="chooser-error">{submitError}</p>}

            <div className="chooser-actions">
              <button
                className="packages-ghost"
                onClick={() => setChoosing(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="package-card__cta"
                onClick={() => submit(choosing, answers)}
                disabled={!chooserReady || submitting}
              >
                {submitting ? 'One moment…' : ctaLabel(choosing)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packages;
